import { useMemo } from 'react';
import { format, isValid } from 'date-fns';

import type {
  ContentfulAsset,
  ContentfulEntry,
  ContentfulTag,
} from '@order/Contentful';
import { useContentfulContentTypeEntry } from '@order/Contentful';
import { TierName } from '@order/graphql';
import { useLocalizationContext } from '@order/Localization';

export const useSweetpassHomeContentfulData = ({
  sweetpassTier,
  firstName,
  joinDate,
}: UseSweetpassHomeLoadedContentProps) => {
  const { t } = useLocalizationContext();

  const {
    data: contentfulData,
    fetching: isLoadingContentfulData,
    error: contentfulError,
  } = useContentfulContentTypeEntry<ContentfulSweetpassHomePage>({
    contentType: SWEETPASS_HOME_PAGE_CONTENT_ID,
    include: 2,
  });

  const sweetpassHomeContentfulData = useMemo(() => {
    const content = parseContent(
      contentfulData?.fields?.content
        ?.map(contentFromContentful)
        ?.filter(Boolean) as readonly SweetpassHomeContent[],
    );

    const formattedJoinDate = isValid(new Date(joinDate ?? ''))
      ? format(new Date(joinDate ?? ''), 'MMMM yyyy')
      : '';

    const infoText =
      content?.sweetpassHomeStats?.infoText ??
      t('sweetpass.home.stats.member-since');

    const noticeText =
      content?.sweetpassHomeStats?.noticeText ??
      t('sweetpass.home.stats.notice');

    const ctaText =
      content?.sweetpassHomeStats?.ctaText ?? t('sweetpass.home.stats.cta');

    return {
      ...content,
      sweetpassHomeStats: {
        id: content?.sweetpassHomeStats?.id ?? 'fallback',
        type: content?.sweetpassHomeStats?.type ?? 'sweetpassHomeStats',
        headerNameText: t('sweetpass.home.header-name', {
          firstName,
        }),
        headerMembershipText: t('sweetpass.home.header-membership', {
          membership:
            sweetpassTier === TierName.SweetpassPlus
              ? 'sweetpass+'
              : 'sweetpass',
        }),
        infoText: infoText?.replace('{sweetpass_join_date}', formattedJoinDate),
        noticeText,
        ctaText,
      },
    };
  }, [contentfulData?.fields?.content, firstName, joinDate, sweetpassTier, t]);

  return {
    isLoadingContentfulData,
    sweetpassHomeContentfulData,
    contentfulError,
  };
};

type UseSweetpassHomeLoadedContentProps = {
  readonly sweetpassTier?: TierName;
  readonly joinDate?: string | null;
  readonly firstName?: string | null;
};

// ––– Home Page –––––––––––––––––––––––––

type ContentfulSweetpassHomePage = {
  readonly content: ReadonlyArray<
    ContentfulEntry<ContentfulSweetpassHomeContent>
  >;
};

// ––– Content (Stats, Actions, Challenges, Rewards) ––––

export type SweetpassHomeContent =
  | SweetpassHomeKnownContent
  | SweetpassHomeUnknownContent;

export type SweetpassHomeKnownContent =
  | SweetpassHomeStatsContent
  | SweetpassHomeActionsRailContent
  | SweetpassHomeChallengesRailContent
  | SweetpassHomeRewardsRailContent;

type ContentfulSweetpassHomeContent =
  | ContentfulSweetpassHomeStats
  | ContentfulSweetpassHomeActionsRail
  | ContentfulSweetpassHomeChallengesRail
  | ContentfulSweetpassHomeRewardsRail;

export type SweetpassHomeUnknownContent = {
  readonly type: string;
  readonly id: string;
};

const SWEETPASS_HOME_PAGE_CONTENT_ID = 'sweetpassHomePage';

const contentFromContentful = (
  entry: ContentfulEntry<ContentfulSweetpassHomeContent>,
): SweetpassHomeContent | null => {
  if (!checkIfContentIsValid(entry)) return null;

  const contentTypeId = entry?.sys?.contentType?.sys?.id;

  if (contentTypeId === 'sweetpassHomeStats') {
    return statsFromContentful(
      entry as ContentfulEntry<ContentfulSweetpassHomeStats>,
    );
  }

  if (contentTypeId === 'sweetpassHomeActionsRail') {
    return actionsRailFromContentful(
      entry as ContentfulEntry<ContentfulSweetpassHomeActionsRail>,
    );
  }

  if (contentTypeId === 'sweetpassHomeChallengesRail') {
    return challengesRailFromContentful(
      entry as ContentfulEntry<ContentfulSweetpassHomeChallengesRail>,
    );
  }

  if (contentTypeId === 'sweetpassHomeRewardsRail') {
    return rewardsRailFromContentful(
      entry as ContentfulEntry<ContentfulSweetpassHomeRewardsRail>,
    );
  }

  return {
    type: contentTypeId,
    id: entry.sys.id,
  };
};

// ––– Stats –––––––––––––––––––––––––––––––––––

export type SweetpassHomeStatsContent = {
  readonly type: 'sweetpassHomeStats';
  readonly id: string;
  readonly headerNameText?: string;
  readonly headerMembershipText?: string;
  readonly infoText: string;
  readonly noticeText: string;
  readonly ctaText: string;
};

type ContentfulSweetpassHomeStats = {
  readonly infoText: string;
  readonly noticeText: string;
  readonly ctaText: string;
};

const statsFromContentful = (
  entry: ContentfulEntry<ContentfulSweetpassHomeStats>,
): SweetpassHomeStatsContent | null => {
  if (!checkIfContentIsValid(entry)) return null;

  return {
    type: 'sweetpassHomeStats',
    id: entry.sys.id,
    infoText: entry.fields.infoText,
    noticeText: entry.fields.noticeText,
    ctaText: entry.fields.ctaText,
  };
};

// ––– Actions Rail ––––––––––––––––––––––––––––

export type SweetpassHomeActionsRailContent = {
  readonly type: 'sweetpassHomeActionsRail';
  readonly id: string;
  readonly titleText: string;
  readonly bodyText?: string;
  readonly actionCards: readonly SweetpassHomeActionCardItem[];
};

type ContentfulSweetpassHomeActionsRail = {
  readonly titleText: string;
  readonly bodyText?: string;
  readonly actionCards: ReadonlyArray<
    ContentfulEntry<ContentfulSweetpassHomeActionCard>
  >;
};

const actionsRailFromContentful = (
  entry: ContentfulEntry<ContentfulSweetpassHomeActionsRail>,
): SweetpassHomeActionsRailContent | null => {
  if (!checkIfContentIsValid(entry)) return null;

  return {
    type: 'sweetpassHomeActionsRail',
    id: entry.sys.id,
    titleText: entry.fields.titleText,
    bodyText: entry.fields.bodyText,
    actionCards: entry.fields.actionCards
      .map(actionCardItemFromContentful)
      .filter(Boolean) as readonly SweetpassHomeActionCardItem[],
  };
};

// ––– Action Card ––––––––––––––––––––––––––––––––––

export type SweetpassHomeActionCardItem = {
  readonly id: string;
  readonly content: {
    readonly headingText: string;
    readonly bodyText: string;
    readonly image: {
      readonly url: string;
      readonly description: string;
    };
  };
  readonly action?: string;
  readonly layout: string;
  readonly theme: string;
  readonly tags?: readonly string[];
};

type ContentfulSweetpassHomeActionCard = {
  readonly headingText: string;
  readonly bodyText: string;
  readonly image: ContentfulAsset;
  readonly action?: string;
  readonly layout: string;
  readonly theme: string;
  readonly tags?: readonly ContentfulTag[];
};

const actionCardItemFromContentful = (
  entry: ContentfulEntry<ContentfulSweetpassHomeActionCard>,
): SweetpassHomeActionCardItem | null => {
  if (!checkIfContentIsValid(entry)) return null;

  const tags = entry.metadata.tags.map((tag) => tag.sys.id);

  return {
    id: entry.sys.id,
    content: {
      headingText: entry.fields.headingText,
      bodyText: entry.fields.bodyText,
      image: {
        description: entry.fields.image.fields.description,
        url: entry.fields.image.fields.file.url,
      },
    },
    action: entry.fields.action,
    layout: entry.fields.layout,
    theme: entry.fields.theme,
    tags,
  };
};

// ––– Challenges Rail ––––––––––––––––––––––––––––

export type SweetpassHomeChallengesRailContent = {
  readonly type: 'sweetpassHomeChallengesRail';
  readonly id: string;
  readonly titleText: string;
  readonly bodyText?: string;
  readonly emptyContentText: string;
  readonly emptyContentImage: {
    readonly url: string;
    readonly description: string;
  };
};

type ContentfulSweetpassHomeChallengesRail = {
  readonly titleText: string;
  readonly bodyText?: string;
  readonly emptyContentText: string;
  readonly emptyContentImage: ContentfulAsset;
};

const challengesRailFromContentful = (
  entry: ContentfulEntry<ContentfulSweetpassHomeChallengesRail>,
): SweetpassHomeChallengesRailContent | null => {
  if (!checkIfContentIsValid(entry)) return null;

  return {
    type: 'sweetpassHomeChallengesRail',
    id: entry.sys.id,
    titleText: entry.fields.titleText,
    bodyText: entry.fields.bodyText,
    emptyContentText: entry.fields.emptyContentText,
    emptyContentImage: {
      description: entry.fields.emptyContentImage.fields.description,
      url: entry.fields.emptyContentImage.fields.file.url,
    },
  };
};

// ––– Rewards Rail ––––––––––––––––––––––––––––

export type SweetpassHomeRewardsRailContent = {
  readonly type: 'sweetpassHomeRewardsRail';
  readonly id: string;
  readonly titleText: string;
  readonly bodyText?: string;
  readonly emptyContentText: string;
  readonly emptyContentImage: {
    readonly url: string;
    readonly description: string;
  };
};

type ContentfulSweetpassHomeRewardsRail = {
  readonly titleText: string;
  readonly bodyText?: string;
  readonly emptyContentText: string;
  readonly emptyContentImage: ContentfulAsset;
};

const rewardsRailFromContentful = (
  entry: ContentfulEntry<ContentfulSweetpassHomeRewardsRail>,
): SweetpassHomeRewardsRailContent | null => {
  if (!checkIfContentIsValid(entry)) return null;

  return {
    type: 'sweetpassHomeRewardsRail',
    id: entry.sys.id,
    titleText: entry.fields.titleText,
    bodyText: entry.fields.bodyText,
    emptyContentText: entry.fields.emptyContentText,
    emptyContentImage: {
      description: entry.fields.emptyContentImage.fields.description,
      url: entry.fields.emptyContentImage.fields.file.url,
    },
  };
};

// ─── Content Validation ────────────────────────────────────────────────

const checkIfContentIsValid = (entry: ContentfulEntry<unknown>) => {
  const hasValidContentId = Boolean(entry?.sys?.id);
  const hasContentFields = Boolean(entry?.fields);

  return hasValidContentId && hasContentFields;
};

// ─── Content Parsing ───────────────────────────────────────────────────

type ParsedContent = {
  readonly sweetpassHomeStats?: SweetpassHomeStatsContent;
  readonly sweetpassHomeActionsRail?: SweetpassHomeActionsRailContent;
  readonly contentItems: ReadonlyArray<
    | SweetpassHomeChallengesRailContent
    | SweetpassHomeRewardsRailContent
    | SweetpassHomeContent
  >;
};

const parseContent = (
  contents?: readonly SweetpassHomeContent[],
): ParsedContent | undefined => {
  if (!contents) return;

  return contents.reduce<ParsedContent>(
    (acc, content) => {
      const key = content.type;

      if (['sweetpassHomeStats', 'sweetpassHomeActionsRail'].includes(key)) {
        return {
          ...acc,
          [key]: content,
        };
      }

      return {
        ...acc,
        contentItems: [...acc.contentItems, content],
      };
    },
    {
      contentItems: [],
    },
  );
};
